import React, { useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Footer from "./Footer";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { Link, useNavigate } from "react-router-dom";
import "../styles/HomePage.css";
import {
  FaComments,
  FaTimes,
  FaWifi,
  FaBed,
  FaUserFriends,
  FaDoorOpen,
  FaCalendarAlt,
  FaChevronRight,
  FaChevronLeft,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaWhatsapp,
  FaCheckCircle,
  FaImage,
  FaSpinner,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import LoadingScreen from "./LoadingScreen";
import {
  resolvePriceFromMap,
  getRoomLimits,
  resolvePerPersonPrice,
  calculateBookingTotal,
  resolveAdvanceBookingAmount,
} from "../utils/priceHelper";
import { callBackend } from "../backend/callBackend";
import { normalizeMediaUrl, fetchWebsiteMedia } from "../backend/mediaService";
import { setMediaData, setMediaLoading } from "../redux/mediaSlice";

// Default room images if missing in JSON

const DEFAULT_ROOM_IMAGES = {
  "Standard AC Room":
    "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FstandardAC%2Froom_Ac_1.jpg?alt=media&token=5ff1f018-4def-4ab8-a438-88840d6f5060",
  "Standard Non-AC Room":
    "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FstandardAC%2Froom_2_ac.jpg?alt=media&token=cc127106-6d2a-4ba2-961c-0c954c4adc0a",
  "Double AC Room":
    "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FdoubleRoomAC%2FRM5.jpg?alt=media&token=ae1846dc-c7b7-4c60-89d7-7eeb3f258cfd",
  "Double Non-AC Room":
    "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FdoubleRoomNonAC%2Fc0224fc2-45a4-4d63-822d-07fa4c7839ab.jpeg?alt=media&token=91bec3ec-7cc8-4bd1-9a69-faaf61d0a5f2",
};


// Room Type URL mapping for /images/:roomType
const ROOM_SLUGS = {
  "Standard AC Room": "standardAC",
  "Standard Non-AC Room": "standardNonAC",
  "Double AC Room": "doubleRoomAC",
  "Double Non-AC Room": "doubleRoomNonAC",
};

const resolveRoomSlug = (title = "") => {
  if (!title) return "standardAC";
  if (ROOM_SLUGS[title]) return ROOM_SLUGS[title];
  const lower = String(title).toLowerCase().trim();
  if (lower.includes("double")) {
    if (lower.includes("non")) return "doubleRoomNonAC";
    return "doubleRoomAC";
  }
  if (lower.includes("standard")) {
    if (lower.includes("non")) return "standardNonAC";
    return "standardAC";
  }
  return "standardAC";
};

// Nearby Attractions
const NEARBY_ATTRACTIONS = [
  {
    name: "Shrivardhan Beach & Peshwe Statue",
    distance: "5 Min Walk (500 m)",
    desc: "Clean black sand beach with sunset points, water sports, and tranquil promenade.",
    image:
      "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=80",
    tag: "Beach",
  },
  {
    name: "Harihareshwar Shiva Temple & Beach",
    distance: "18 km (25 Mins)",
    desc: "Ancient temple known as 'Dakshin Kashi', famous for temple parikrama along cliffs.",
    image:
      "https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=600&q=80",
    tag: "Heritage",
  },
  {
    name: "Diveagar Beach & Suvarna Ganesh Temple",
    distance: "16 km (20 Mins)",
    desc: "Scenic coastline with pristine waters, ATV rides, jet skis, and local coconut stalls.",
    image:
      "https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&w=600&q=80",
    tag: "Adventure",
  },
  {
    name: "Aravi Beach & Cliff View",
    distance: "4 km (8 Mins)",
    desc: "Secluded pristine beach ideal for sunset photography and coastal tranquility.",
    image:
      "https://images.unsplash.com/photo-1510414842594-a61c69b5ae57?auto=format&fit=crop&w=600&q=80",
    tag: "Scenic",
  },
];

export default function HomePage() {
  const hotel = useSelector((state) => state.hotel.data) || {};
  const configData = useSelector((state) => state.config?.data);
  const mediaState = useSelector((state) => state.media) || {};
  const loading = useSelector((state) => state.hotel.loading);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const coverPhotos = mediaState.coverPhotos || {};
  const backendRooms = mediaState.rooms || {};

  // Fetch website media if not yet loaded in Redux
  useEffect(() => {
    if (!mediaState.data && !mediaState.loading) {
      dispatch(setMediaLoading(true));
      fetchWebsiteMedia({ isVisible: true })
        .then((res) => {
          if (res) {
            dispatch(setMediaData(res));
          }
        })
        .catch((err) => console.warn("Failed to fetch media on HomePage:", err));
    }
  }, [mediaState.data, mediaState.loading, dispatch]);


  // Floating widget & Nav State
  const [contactOpen, setContactOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [roomFilter, setRoomFilter] = useState("all");
  const [galleryFilter, setGalleryFilter] = useState("all");

  // Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Search Bar state
  const todayStr = new Date().toISOString().split("T")[0];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0];

  const [checkin, setCheckin] = useState(todayStr);
  const [checkout, setCheckout] = useState(tomorrowStr);
  const [selectedRoomType, setSelectedRoomType] = useState("Standard AC Room");
  const [guestCount, setGuestCount] = useState("2 Guests");

  const roomLimits = getRoomLimits(configData, selectedRoomType);

  const handleRoomTypeChange = (newRoom) => {
    setSelectedRoomType(newRoom);
    const limits = getRoomLimits(configData, newRoom);
    const currentNum = parseInt(guestCount.replace(/\D/g, "") || "2");
    if (currentNum > limits.maxLimit) {
      setGuestCount(`${limits.maxLimit} Guests`);
    }
  };

  // Dynamic price helper from config API with semantic resolution
  const getRoomPrice = (roomTitle) => {
    return resolvePriceFromMap(configData?.price_map, roomTitle, 2500);
  };

  // Availability checking state
  const [isCheckingAvail, setIsCheckingAvail] = useState(false);
  const [availResult, setAvailResult] = useState(null);

  // Direct Proceed to Booking
  const proceedToBooking = () => {
    const params = new URLSearchParams({
      checkin,
      checkout,
      room: selectedRoomType,
      guests: guestCount.replace(/\D/g, "") || "2",
    });
    navigate(`/booking?${params.toString()}`);
  };

  // Handle Search Submission -> Check Availability first
  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    setIsCheckingAvail(true);
    setAvailResult(null);

    try {
      const res = await callBackend("backendBookingService", "checkAvailability", {
        checkinDate: checkin,
        checkoutDate: checkout,
        roomCount: 1,
        roomType: selectedRoomType,
      });

      const canBook = res?.data?.canBook !== false;
      const maxRooms = res?.data?.maxRoomLimit || 7;
      const bookedRooms = res?.data?.alreadyBookedRooms || 0;
      const remaining = Math.max(0, maxRooms - bookedRooms);

      if (canBook && remaining > 0) {
        setAvailResult({
          available: true,
          remaining,
          message: `Great news! Rooms are available for ${checkin} to ${checkout}.`,
        });

        // Smooth auto-redirect after 1.4s
        setTimeout(() => {
          proceedToBooking();
        }, 1400);
      } else {
        setAvailResult({
          available: false,
          remaining,
          message: `Rooms are fully booked for ${checkin} to ${checkout}. Please choose alternate dates.`,
        });
      }
    } catch (err) {
      console.warn("Availability check error, proceeding to booking:", err);
      proceedToBooking();
    } finally {
      setIsCheckingAvail(false);
    }
  };


  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 40) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Collapse navbar on link click
  useEffect(() => {
    const navCollapse = document.getElementById("navLinks");
    if (!navCollapse || !window.bootstrap) return;
    const bsCollapse = window.bootstrap.Collapse.getOrCreateInstance(navCollapse, {
      toggle: false,
    });

    const navLinks = document.querySelectorAll(".nav-link-custom");
    const handleLinkClick = () => bsCollapse.hide();
    navLinks.forEach((link) => link.addEventListener("click", handleLinkClick));
    return () =>
      navLinks.forEach((link) =>
        link.removeEventListener("click", handleLinkClick)
      );
  }, []);

  // Dynamic Cover image helper from backend media (prioritizes live backend media over json)
  const getRoomCoverImage = (roomTitle, fallback = "") => {
    const slug = resolveRoomSlug(roomTitle);
    const coverObj =
      coverPhotos[slug] ||
      coverPhotos[roomTitle] ||
      coverPhotos[slug.toLowerCase()] ||
      backendRooms[slug]?.cover ||
      backendRooms[roomTitle]?.cover ||
      backendRooms[slug.toLowerCase()]?.cover;

    if (coverObj) {
      const url = normalizeMediaUrl(coverObj);
      if (url) return url;
    }
    return fallback || DEFAULT_ROOM_IMAGES[roomTitle] || DEFAULT_ROOM_IMAGES["Standard AC Room"];
  };

  // 1. Gather all candidate media items from backend
  const allBackendItems = mediaState.allMedia || mediaState.gallery || [];
  const amenitiesItems =
    mediaState.rooms?.amenities?.allMedia ||
    mediaState.rooms?.amenities?.images ||
    [];

  const combinedCandidates = [
    ...(mediaState.featuredMedia || []),
    ...allBackendItems,
    ...amenitiesItems,
  ];

  const sourceList =
    combinedCandidates.length > 0
      ? combinedCandidates
      : mediaState.coverList && mediaState.coverList.length > 0
        ? mediaState.coverList
        : [
          "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FstandardAC%2Froom_Ac_1.jpg?alt=media&token=5ff1f018-4def-4ab8-a438-88840d6f5060",
          "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FstandardAC%2Froom_2_ac.jpg?alt=media&token=cc127106-6d2a-4ba2-961c-0c954c4adc0a",
          "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FdoubleRoomAC%2FRM5.jpg?alt=media&token=ae1846dc-c7b7-4c60-89d7-7eeb3f258cfd",
          "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/rooms%2FdoubleRoomAC%2FRM4.jpg?alt=media&token=0bf942b5-6135-455b-8c28-b35f76597cb2",
        ];

  // 2. Strict Deduplication and Tag Mapping
  const seenMediaKeys = new Set();
  const galleryItems = [];

  sourceList.forEach((item, i) => {
    const url = normalizeMediaUrl(item);
    if (!url) return;

    const urlKey = url.split("?")[0].toLowerCase().trim();
    const docId = typeof item === "object" && item.id ? String(item.id).trim() : "";
    const fileName =
      typeof item === "object" && item.fileName
        ? String(item.fileName).toLowerCase().trim()
        : "";

    if (
      seenMediaKeys.has(urlKey) ||
      (docId && seenMediaKeys.has(docId)) ||
      (fileName && seenMediaKeys.has(fileName))
    ) {
      return; // Skip duplicate!
    }
    seenMediaKeys.add(urlKey);
    if (docId) seenMediaKeys.add(docId);
    if (fileName) seenMediaKeys.add(fileName);

    const rawTitle =
      typeof item === "object"
        ? item.title || item.caption || item.name || ""
        : "";
    const rawCat =
      typeof item === "object"
        ? String(
          item.roomId ||
          item.roomType ||
          item.category ||
          item.roomCategory ||
          ""
        ).toLowerCase()
        : "";

    const tags =
      typeof item === "object" && Array.isArray(item.tags)
        ? item.tags.map((t) => String(t).trim())
        : [];
    const tagsLower = tags.map((t) => t.toLowerCase());

    // Strictly check tags ONLY (No fallback to room category if tags array is empty)
    const hasRoomTag = tagsLower.some(
      (t) =>
        t === "rooms & suites" ||
        t === "room & suites" ||
        t === "rooms and suites" ||
        t === "room and suits" ||
        t === "rooms & suites "
    );

    const hasSurroundingsTag = tagsLower.some(
      (t) =>
        t === "surroundings" ||
        t === "surrounding" ||
        t === "surroundings & beach" ||
        t === "surroundings and beach"
    );

    const isVideo =
      typeof item === "object" &&
      (item.mediaType === "video" ||
        tagsLower.some((t) => t === "video" || t.includes("video") || t.includes("walkthrough")));

    const isFeatured = Boolean(
      typeof item === "object" &&
      (item.isFeatured ||
        item.isCover ||
        tagsLower.some((t) => t === "featured" || t.includes("featured") || t === "cover"))
    );

    const isSurroundings = hasSurroundingsTag && !hasRoomTag;
    const category = isSurroundings ? "surroundings" : "rooms";

    const defaultTitle = isSurroundings
      ? "Homestay Surroundings"
      : rawCat.includes("double")
        ? "Double Deluxe Room"
        : "Standard Comfort Room";

    const title =
      rawTitle && typeof rawTitle === "string" && rawTitle.trim().length > 0
        ? rawTitle.trim()
        : defaultTitle;

    galleryItems.push({
      id: docId || `media_${i}`,
      url,
      title,
      category,
      mediaType: isVideo ? "video" : "image",
      tags,
      isCover: typeof item === "object" ? Boolean(item.isCover) : false,
      isFeatured,
      hasRoomTag,
      hasSurroundingsTag,
      isVideo,
    });
  });

  const featuredItems = galleryItems.filter((i) => i.isFeatured);
  const displayFeaturedPool =
    featuredItems.length > 0 ? featuredItems : galleryItems;

  const roomMediaCount = galleryItems.filter((i) => i.hasRoomTag).length;
  const surroundingsMediaCount = galleryItems.filter(
    (i) => i.hasSurroundingsTag
  ).length;
  const videoMediaCount = galleryItems.filter((i) => i.isVideo).length;

  const filteredGallery =
    galleryFilter === "all"
      ? displayFeaturedPool
      : galleryFilter === "rooms"
        ? galleryItems.filter((item) => item.hasRoomTag)
        : galleryFilter === "surroundings"
          ? galleryItems.filter((item) => item.hasSurroundingsTag)
          : galleryFilter === "videos"
            ? galleryItems.filter((item) => item.isVideo)
            : displayFeaturedPool;

  // Keyboard navigation for Lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        setLightboxIndex((prev) => (prev > 0 ? prev - 1 : filteredGallery.length - 1));
      } else if (e.key === "ArrowRight") {
        setLightboxIndex((prev) => (prev < filteredGallery.length - 1 ? prev + 1 : 0));
      } else if (e.key === "Escape") {
        setLightboxOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, filteredGallery.length]);

  // Calculate Duration in nights
  const calculateNights = () => {
    try {
      const d1 = new Date(checkin);
      const d2 = new Date(checkout);
      const diff = Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 1;
    } catch {
      return 1;
    }
  };

  const nights = calculateNights();
  const parsedGuests = parseInt(guestCount.replace(/\D/g, "") || "2");
  const bookingCalc = calculateBookingTotal({
    configData,
    roomIdentifier: selectedRoomType,
    roomCount: 1,
    guestCount: parsedGuests,
    nights,
  });
  const estimatedTotal = bookingCalc.totalAmount;


  const standardMax = Number(configData?.standard_room_max_limit ?? 5);
  const doubleMax = Number(configData?.double_room_max_limit ?? 10);

  // Rooms filtering with dynamic cover photo support
  const rawPackages = (hotel.packages && hotel.packages.length > 0)
    ? hotel.packages.map((pkg) => ({
      ...pkg,
      image: getRoomCoverImage(pkg.title, pkg.image),
    }))
    : [
      {
        title: "Standard AC Room",
        desc: "Cozy air-conditioned room with king bed, attached modern bath, and peaceful garden view. Ideal for couples & small families.",
        image: getRoomCoverImage("Standard AC Room"),
        maxGuests: standardMax,
        type: "ac",
      },
      {
        title: "Standard Non-AC Room",
        desc: "Budget-friendly, airy room equipped with ceiling fans, attached clean bathroom, and pleasant natural coastal breeze.",
        image: getRoomCoverImage("Standard Non-AC Room"),
        maxGuests: standardMax,
        type: "non-ac",
      },
      {
        title: "Double AC Room",
        desc: "Spacious deluxe suite with 2 double beds, powerful AC, balcony sit-out, perfect for families and larger groups.",
        image: getRoomCoverImage("Double AC Room"),
        maxGuests: doubleMax,
        type: "ac",
      },
      {
        title: "Double Non-AC Room",
        desc: "Large family room with cross-ventilation, multiple double beds, attached bath, and ample space for group get-togethers.",
        image: getRoomCoverImage("Double Non-AC Room"),
        maxGuests: doubleMax,
        type: "non-ac",
      },
    ];

  const packagesList = rawPackages.map((pkg) => {
    const limits = getRoomLimits(configData, pkg.title);
    return {
      ...pkg,
      maxGuests: limits.maxLimit || pkg.maxGuests || standardMax,
    };
  });

  const filteredPackages = packagesList.filter((pkg) => {
    if (roomFilter === "all") return true;
    if (roomFilter === "ac") return pkg.title.toLowerCase().includes("ac") && !pkg.title.toLowerCase().includes("non-ac");
    if (roomFilter === "non-ac") return pkg.title.toLowerCase().includes("non-ac");
    if (roomFilter === "family") return (pkg.maxGuests >= 4 || pkg.title.toLowerCase().includes("double"));
    return true;
  });

  // Dynamic Nearby Attractions: Map uploaded attraction photos from backend API
  const attractionMediaList =
    mediaState.rooms?.attractions?.allMedia ||
    mediaState.rooms?.attractions?.images ||
    (mediaState.allMedia || []).filter(
      (m) =>
        m.roomId === "attractions" ||
        m.roomType === "attractions" ||
        m.category === "attractions" ||
        (Array.isArray(m.tags) &&
          m.tags.some(
            (t) =>
              String(t).toLowerCase() === "attractions" ||
              String(t).toLowerCase() === "attraction" ||
              String(t).toLowerCase() === "nearby" ||
              String(t).toLowerCase() === "shrivardhan" ||
              String(t).toLowerCase() === "harihareshwar" ||
              String(t).toLowerCase() === "diveagar" ||
              String(t).toLowerCase() === "aravi"
          ))
    );

  const dynamicAttractions = NEARBY_ATTRACTIONS.map((attr) => {
    const nameLower = attr.name.toLowerCase();
    const matched = attractionMediaList.find((item) => {
      const title = String(item.title || "").toLowerCase();
      const tags = Array.isArray(item.tags)
        ? item.tags.map((t) => String(t).toLowerCase())
        : [];
      const fileName = String(item.fileName || "").toLowerCase();

      if (
        nameLower.includes("shrivardhan") &&
        (title.includes("shrivardhan") ||
          tags.includes("shrivardhan") ||
          fileName.includes("shrivardhan"))
      ) {
        return true;
      }
      if (
        nameLower.includes("harihareshwar") &&
        (title.includes("harihareshwar") ||
          tags.includes("harihareshwar") ||
          fileName.includes("harihareshwar"))
      ) {
        return true;
      }
      if (
        nameLower.includes("diveagar") &&
        (title.includes("diveagar") ||
          tags.includes("diveagar") ||
          fileName.includes("diveagar"))
      ) {
        return true;
      }
      if (
        nameLower.includes("aravi") &&
        (title.includes("aravi") ||
          tags.includes("aravi") ||
          fileName.includes("aravi"))
      ) {
        return true;
      }
      return false;
    });

    const dynamicUrl = matched ? normalizeMediaUrl(matched) : null;
    return {
      ...attr,
      image: dynamicUrl || attr.image,
    };
  });

  // Append any custom attractions added in backend that are not part of standard 4
  attractionMediaList.forEach((item) => {
    const title = item.title || "";
    const titleLower = title.toLowerCase();
    const isAlreadyMatched =
      titleLower.includes("shrivardhan") ||
      titleLower.includes("harihareshwar") ||
      titleLower.includes("diveagar") ||
      titleLower.includes("aravi");

    const imgUrl = normalizeMediaUrl(item);
    if (!isAlreadyMatched && imgUrl) {
      const tag = item.tags?.[0] || "Sightseeing";
      dynamicAttractions.push({
        name: title || "Nearby Scenic Spot",
        distance: item.caption || "Nearby Sightseeing",
        desc: item.desc || "Explore beautiful coastal spots and heritage around Shrivardhan.",
        image: imgUrl,
        tag,
      });
    }
  });

  if (loading) return <LoadingScreen />;

  const hotelName = hotel.name || "Ganesh Holiday Homes";
  const hotelPhone = hotel.phone || "+91 9673150591";
  const cleanPhone = hotelPhone.replace(/[^0-9]/g, "");

  return (
    <HelmetProvider>
      <div style={{ overflowX: "hidden", minHeight: "100vh" }}>
        {/* SEO Metadata */}
        <Helmet>
          <title>{hotelName} | Modern Beach Homestay in Shrivardhan</title>
          <meta
            name="description"
            content={`${hotelName} offers cozy AC & family rooms, authentic Konkani home meals, and a 5-minute walk to Shrivardhan Beach.`}
          />
          <meta property="og:title" content={`${hotelName} - Shrivardhan Beach Stay`} />
          <meta property="og:description" content={hotel.aboutText || "Serene & family-friendly beach stay near Shrivardhan Beach."} />
          <meta property="og:image" content={hotel.heroImage} />
        </Helmet>

        {/* 1. Modern Glassmorphic Sticky Header */}
        <nav
          className={`navbar navbar-expand-lg navbar-custom sticky-top ${isScrolled ? "navbar-scrolled" : ""
            }`}
        >
          <div className="container">
            <Link className="navbar-brand d-flex align-items-center gap-2" to="/">
              {hotel.logoUrl ? (
                <img
                  src={hotel.logoUrl}
                  alt={hotelName}
                  className="brand-logo-img"
                />
              ) : (
                <div
                  style={{
                    width: "42px",
                    height: "42px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #ff4757, #ff6b6b)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  G
                </div>
              )}
              <div className="d-flex flex-column">
                <span className="brand-title">{hotelName}</span>
                <span className="brand-subtitle">Beachside Retreat • Shrivardhan</span>
              </div>
            </Link>

            {/* Mobile Toggle Button */}
            <button
              className="navbar-toggler border-0 shadow-none"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navLinks"
              aria-controls="navLinks"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            {/* Nav Menu */}
            <div className="collapse navbar-collapse" id="navLinks">
              <ul className="navbar-nav mx-auto mb-2 mb-lg-0 align-items-lg-center">
                <li className="nav-item">
                  <Link className="nav-link-custom" to="/rooms">
                    Rooms & Tariffs
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link-custom" to="/about-us">
                    About Us
                  </Link>
                </li>
                <li className="nav-item">
                  <a className="nav-link-custom" href="#amenities">
                    Amenities
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link-custom" href="#gallery">
                    Gallery
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link-custom" href="#attractions">
                    Attractions
                  </a>
                </li>
                <li className="nav-item">
                  <Link className="nav-link-custom" to="/contact-us">
                    Contact & Location
                  </Link>
                </li>
                <li className="nav-item">
                  <a className="nav-link-custom" href="#faq">
                    FAQ
                  </a>
                </li>
              </ul>

              <div className="d-flex align-items-center gap-3">
                <a href={`tel:${cleanPhone}`} className="nav-phone-btn d-none d-xl-flex">
                  <FaPhoneAlt className="text-danger" />
                  <span>{hotelPhone}</span>
                </a>
                <Link to="/booking" className="btn-luxury-primary">
                  <FaCalendarAlt />
                  <span>Book Now</span>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* 2. Hero Section */}
        <section
          id="hero"
          className="hero-section"
          style={{
            backgroundImage: `url(${hotel.heroImage ||
              "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/resources%2Fheroimages%2Fsea.png?alt=media&token=f4f4710f-1be6-4098-95df-442618390e07"
              })`,
          }}
        >
          <div className="hero-overlay"></div>
          <div className="container">
            <div className="hero-content">
              <div className="hero-badge animate-fade-in">
                <span>🏨 Short-Term Accommodation in Shrivardhan • 🏖️ 5 Min Walk to Beach</span>
              </div>

              <h1 className="hero-title animate-slide-up">
                Ganesh Holiday Homes – Beachside Accommodation in Shrivardhan
              </h1>

              <p className="hero-tagline animate-slide-up">
                We provide comfortable rooms for short-term stays in Shrivardhan, Maharashtra. Guests can book accommodation directly through our website with transparent pricing in ₹ INR, homely Konkani meals, and instant confirmation.
              </p>

              <div className="hero-cta-group">
                <Link to="/booking" className="btn-luxury-primary btn-lg shadow-lg">
                  <FaCalendarAlt />
                  <span>Reserve Your Room</span>
                </Link>
                <a
                  href={`https://wa.me/${cleanPhone}?text=Hi%20Ganesh%20Holiday%20Homes,%20I%20would%20like%20to%20inquire%20about%20room%20availability`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-luxury-outline btn-lg"
                >
                  <FaWhatsapp className="text-success" />
                  <span>Chat on WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Floating Interactive Booking & Availability Search Widget */}
        <div className="container search-widget-wrapper">
          <div className="search-widget-card">
            <form onSubmit={handleSearchSubmit}>
              <div className="row g-3 align-items-end">
                {/* Check In */}
                <div className="col-6 col-lg-3">
                  <label className="search-field-label">
                    <FaCalendarAlt className="text-danger" />
                    <span>Check-In</span>
                  </label>
                  <input
                    type="date"
                    className="search-input"
                    value={checkin}
                    min={todayStr}
                    onChange={(e) => {
                      setCheckin(e.target.value);
                      if (e.target.value >= checkout) {
                        const nextDay = new Date(e.target.value);
                        nextDay.setDate(nextDay.getDate() + 1);
                        setCheckout(nextDay.toISOString().split("T")[0]);
                      }
                    }}
                    required
                  />
                </div>

                {/* Check Out */}
                <div className="col-6 col-lg-3">
                  <label className="search-field-label">
                    <FaCalendarAlt className="text-danger" />
                    <span>Check-Out</span>
                  </label>
                  <input
                    type="date"
                    className="search-input"
                    value={checkout}
                    min={checkin || todayStr}
                    onChange={(e) => setCheckout(e.target.value)}
                    required
                  />
                </div>

                {/* Room Type Selection */}
                <div className="col-6 col-lg-2">
                  <label className="search-field-label">
                    <FaBed className="text-danger" />
                    <span>Room</span>
                  </label>
                  <select
                    className="search-input form-select"
                    value={selectedRoomType}
                    onChange={(e) => handleRoomTypeChange(e.target.value)}
                  >
                    <option value="Standard AC Room">Standard AC</option>
                    <option value="Standard Non-AC Room">Standard Non-AC</option>
                    <option value="Double AC Room">Double AC</option>
                    <option value="Double Non-AC Room">Double Non-AC</option>
                  </select>
                </div>

                {/* Guests Count */}
                <div className="col-6 col-lg-2">
                  <label className="search-field-label">
                    <FaUserFriends className="text-danger" />
                    <span>Guests</span>
                  </label>
                  <select
                    className="search-input form-select"
                    value={guestCount}
                    onChange={(e) => setGuestCount(e.target.value)}
                  >
                    {Array.from({ length: roomLimits.maxLimit }, (_, i) => i + 1).map((num) => {
                      const isExtra = num > roomLimits.noCostLimit;
                      const extraRate = resolvePerPersonPrice(
                        configData?.per_person_price_map,
                        selectedRoomType,
                        200
                      );
                      return (
                        <option key={num} value={`${num} Guest${num > 1 ? "s" : ""}`}>
                          {num} Guest{num > 1 ? "s" : ""}{isExtra ? ` (+₹${extraRate}/n)` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Submit / Check Availability */}
                <div className="col-12 col-lg-2">
                  <button
                    type="submit"
                    className="btn-search-submit"
                    disabled={isCheckingAvail}
                  >
                    {isCheckingAvail ? (
                      <>
                        <FaSpinner className="spinner-border spinner-border-sm me-1" />
                        <span>Checking...</span>
                      </>
                    ) : (
                      <>
                        <span>Check & Book</span>
                        <FaChevronRight />
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Availability Feedback Box */}
              {availResult && (
                <div
                  className={`availability-feedback-box ${availResult.available ? "available" : "unavailable"
                    }`}
                >
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className={`avail-icon-circle ${availResult.available
                          ? "bg-success text-white"
                          : "bg-danger text-white"
                          }`}
                      >
                        {availResult.available ? (
                          <FaCheckCircle />
                        ) : (
                          <FaExclamationTriangle />
                        )}
                      </div>
                      <div>
                        <div
                          className={`avail-title mb-1 ${availResult.available ? "text-success" : "text-danger"
                            }`}
                        >
                          {availResult.available
                            ? "🎉 Rooms Available!"
                            : "⚠️ Fully Booked / Limited Availability"}
                        </div>
                        <div className="avail-desc">
                          {availResult.message}
                          {availResult.available && (
                            <span className="fw-semibold text-success ms-1">
                              Redirecting to instant reservation...
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {availResult.available ? (
                        <button
                          type="button"
                          onClick={proceedToBooking}
                          className="btn btn-success fw-bold px-4 py-2 rounded-pill shadow-sm"
                        >
                          Reserve Now →
                        </button>
                      ) : (
                        <>
                          <a
                            href={`tel:${hotel.phone || "+919422090466"}`}
                            className="btn btn-outline-danger fw-bold px-3 py-2 rounded-pill"
                          >
                            <FaPhoneAlt className="me-1" /> Call Host
                          </a>
                          <button
                            type="button"
                            onClick={() => setAvailResult(null)}
                            className="btn btn-light border fw-bold px-3 py-2 rounded-pill"
                          >
                            Modify Dates
                          </button>
                          <button
                            type="button"
                            onClick={proceedToBooking}
                            className="btn btn-danger fw-bold px-3 py-2 rounded-pill shadow-sm"
                          >
                            Proceed Anyway
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Price Estimate Live Snippet */}
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3 pt-2 border-top">
                <div className="small text-muted d-flex align-items-center gap-2">
                  <FaCheckCircle className="text-success" />
                  <span>
                    <strong>{nights} Night{nights > 1 ? "s" : ""}</strong> Stay • {guestCount}
                  </span>
                </div>
                <div className="small text-dark font-sans">
                  Estimated Total: <span className="fw-bold text-danger fs-6">₹{estimatedTotal.toLocaleString("en-IN")}</span>
                  {bookingCalc.extraGuests > 0 && (
                    <span className="badge bg-light text-danger border ms-1">
                      (Incl. +₹{(bookingCalc.extraCostPerNight * nights).toLocaleString("en-IN")} extra guest fee)
                    </span>
                  )}
                  <span className="text-muted ms-1">(Token Advance ₹{(resolveAdvanceBookingAmount(configData, 1000) * nights).toLocaleString("en-IN")} to reserve)</span>
                </div>
              </div>
            </form>
          </div>
        </div>


        {/* 4. Value Propositions / Highlights Ribbon */}
        <section className="py-2">
          <div className="container">
            <div className="highlights-container">
              <div className="highlight-card">
                <div className="highlight-icon-wrap">🏖️</div>
                <div className="highlight-text-content">
                  <h4 className="highlight-title">5-Min Beach Walk</h4>
                  <p className="highlight-desc">Stroll directly to Shrivardhan beach for sunset & water sports.</p>
                </div>
              </div>

              <div className="highlight-card">
                <div className="highlight-icon-wrap">🍲</div>
                <div className="highlight-text-content">
                  <h4 className="highlight-title">Homely Konkani Meals</h4>
                  <p className="highlight-desc">Authentic fresh fish fry, Solkadhi, and veg Konkani thali on request.</p>
                </div>
              </div>

              <div className="highlight-card">
                <div className="highlight-icon-wrap">❄️</div>
                <div className="highlight-text-content">
                  <h4 className="highlight-title">100% AC & Backup</h4>
                  <p className="highlight-desc">Uninterrupted comfort with cooling AC and power inverter backup.</p>
                </div>
              </div>

              <div className="highlight-card">
                <div className="highlight-icon-wrap">🅿️</div>
                <div className="highlight-text-content">
                  <h4 className="highlight-title">Free Gated Parking</h4>
                  <p className="highlight-desc">Spacious & secure on-premise private parking for cars and SUVs.</p>
                </div>
              </div>

              <div className="highlight-card">
                <div className="highlight-icon-wrap">📶</div>
                <div className="highlight-text-content">
                  <h4 className="highlight-title">High-Speed Wi-Fi</h4>
                  <p className="highlight-desc">Seamless Wi-Fi connectivity for workations and sharing memories.</p>
                </div>
              </div>

              <div className="highlight-card">
                <div className="highlight-icon-wrap">👨‍👩‍👧‍👦</div>
                <div className="highlight-text-content">
                  <h4 className="highlight-title">Family & Group Safe</h4>
                  <p className="highlight-desc">Peaceful, gated private atmosphere tailored for family vacations.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Rooms & Suites Showcase */}
        <section id="rooms" className="py-4">
          <div className="container">
            <div className="section-header">
              <span className="section-pill-badge">Accommodations</span>
              <h2 className="section-main-title">Handcrafted Rooms & Family Suites</h2>
              <p className="section-subtitle">
                Designed for ultimate comfort with plush bedding, attached private bathrooms, and tranquil coastal vibes.
              </p>
              <div className="mt-2 mb-3">
                <Link to="/rooms" className="btn btn-sm btn-outline-danger rounded-pill px-3 fw-semibold">
                  📋 View Full Room Tariffs & Inclusions →
                </Link>
              </div>


              {/* Filter Tabs */}
              <div className="room-filter-tabs">
                <button
                  className={`room-tab-btn ${roomFilter === "all" ? "active" : ""}`}
                  onClick={() => setRoomFilter("all")}
                >
                  All Rooms
                </button>
                <button
                  className={`room-tab-btn ${roomFilter === "ac" ? "active" : ""}`}
                  onClick={() => setRoomFilter("ac")}
                >
                  AC Comfort
                </button>
                <button
                  className={`room-tab-btn ${roomFilter === "family" ? "active" : ""}`}
                  onClick={() => setRoomFilter("family")}
                >
                  Family & Group Suites
                </button>
                <button
                  className={`room-tab-btn ${roomFilter === "non-ac" ? "active" : ""}`}
                  onClick={() => setRoomFilter("non-ac")}
                >
                  Budget Non-AC
                </button>
              </div>
            </div>

            {/* Rooms Grid */}
            <div className="row g-4">
              {filteredPackages.map((pkg, idx) => {
                const roomSlug = resolveRoomSlug(pkg.title);
                const roomImage = getRoomCoverImage(pkg.title, pkg.image);
                const isAc = pkg.title.toLowerCase().includes("ac") && !pkg.title.toLowerCase().includes("non-ac");
                const dynamicPrice = getRoomPrice(pkg.title);
                const priceFormatted = `₹${dynamicPrice.toLocaleString("en-IN")}/night`;


                return (
                  <div className="col-12 col-md-6 col-lg-3" key={idx}>
                    <div className="room-card-modern">
                      <div className="room-card-image-wrap">
                        <img
                          src={roomImage}
                          alt={pkg.title}
                          className="room-card-img"
                          loading="lazy"
                        />
                        <span className="room-tag-badge">
                          {isAc ? "❄️ AC Deluxe" : "🍃 Airy Comfort"}
                        </span>
                        <div className="room-price-badge">
                          <span className="room-price-value">
                            {priceFormatted.replace("/night", "")}
                          </span>
                          <span className="room-price-unit"> /night</span>
                        </div>
                      </div>

                      <div className="room-card-body">
                        <h3 className="room-card-title">{pkg.title}</h3>
                        <p className="room-card-desc">{pkg.desc}</p>

                        <div className="room-specs-grid">
                          <div className="room-spec-item">
                            <FaUserFriends className="room-spec-icon" />
                            <span>Max {pkg.maxGuests} Guests</span>
                          </div>
                          <div className="room-spec-item">
                            <FaBed className="room-spec-icon" />
                            <span>{pkg.title.toLowerCase().includes("double") ? "2 Double Beds" : "1 King Bed"}</span>
                          </div>
                          <div className="room-spec-item">
                            <FaWifi className="room-spec-icon" />
                            <span>Free High-Speed Wi-Fi</span>
                          </div>
                          <div className="room-spec-item">
                            <FaDoorOpen className="room-spec-icon" />
                            <span>Attached Bath & Balcony</span>
                          </div>
                        </div>

                        <div className="room-card-actions">
                          <Link
                            to={`/images/${roomSlug}`}
                            className="btn-room-photos"
                          >
                            <FaImage className="me-1" /> Photos
                          </Link>
                          <Link
                            to={`/booking?room=${encodeURIComponent(pkg.title)}`}
                            className="btn-room-book"
                          >
                            Book Room
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 6. Authentic Konkani Experience & Hospitality */}
        <section id="amenities" className="py-5">
          <div className="container">
            <div className="section-header">
              <span className="section-pill-badge">Why Stay With Us</span>
              <h2 className="section-main-title">Unmatched Konkan Hospitality</h2>
              <p className="section-subtitle">
                At Ganesh Holiday Homes, we provide the warmth of home-style hospitality paired with clean, modern coastal amenities.
              </p>
            </div>

            <div className="row g-4">
              <div className="col-12 col-md-4">
                <div className="experience-card-glass">
                  <span className="experience-icon-badge">🏖️</span>
                  <h3 className="h4 fw-bold mb-3 text-dark">5-Minute Beach Access</h3>
                  <p className="text-muted">
                    No long commutes or driving needed! Walk 500 meters to Shrivardhan Beach for refreshing morning swims, sunset strolls, and beach sports.
                  </p>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="experience-card-glass">
                  <span className="experience-icon-badge">🍛</span>
                  <h3 className="h4 fw-bold mb-3 text-dark">Homemade Konkani Food</h3>
                  <p className="text-muted">
                    Savor freshly cooked authentic coastal dishes: Surmai & Pomfret fry, Solkadhi, Ukadiche Modak, Puran Poli, and wholesome vegetarian thalis upon request.
                  </p>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="experience-card-glass">
                  <span className="experience-icon-badge">🌴</span>
                  <h3 className="h4 fw-bold mb-3 text-dark">Lush Coconut Grove Sit-out</h3>
                  <p className="text-muted">
                    Relax under gentle palm tree shade in our peaceful private lawn. Safe play space for kids, open seating for morning chai and family conversations.
                  </p>
                </div>
              </div>
            </div>

            {/* About Story Row */}
            {hotel.aboutParagraphs && (
              <div className="row g-4 align-items-center mt-5 pt-3">
                <div className="col-12 col-lg-6">
                  <div className="position-relative">
                    <img
                      src={
                        hotel.aboutImage ||
                        "https://firebasestorage.googleapis.com/v0/b/ganesh-holiday-homes.firebasestorage.app/o/resources%2Faboutusimage%2Faboutusjpeg.jpeg?alt=media&token=59bdff6d-a7cd-4080-9bd2-d3f9d7568cfb"
                      }
                      alt="About Ganesh Holiday Homes"
                      className="img-fluid rounded-4 shadow-lg w-100"
                      style={{ maxHeight: "420px", objectFit: "cover" }}
                    />
                    <div
                      className="position-absolute bottom-0 start-0 m-3 p-3 rounded-3 text-white"
                      style={{ background: "rgba(11, 25, 44, 0.85)", backdropFilter: "blur(10px)" }}
                    >
                      <h6 className="fw-bold mb-0 text-warning">5000+ Happy Guests</h6>
                      <small>Welcoming families to Shrivardhan since 2018</small>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-lg-6">
                  <span className="section-pill-badge">Our Story</span>
                  <h2 className="section-main-title">{hotel.aboutTitle || "Welcome to Ganesh Holiday Homes"}</h2>
                  {hotel.aboutParagraphs.map((p, i) => (
                    <p key={i} className="text-muted mb-3" style={{ fontSize: "1.02rem" }}>
                      {p}
                    </p>
                  ))}
                  <div className="mt-4 d-flex gap-3">
                    <Link to="/booking" className="btn-luxury-primary">
                      Plan Your Getaway
                    </Link>
                    <a href={`tel:${cleanPhone}`} className="btn-luxury-outline text-dark border-dark">
                      Call Host: {hotelPhone}
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* 7. Gallery & Virtual Tour */}
        <section id="gallery" className="py-5">
          <div className="container">
            <div className="section-header">
              <span className="section-pill-badge">⭐ Featured Highlights</span>
              <h2 className="section-main-title">Glimpse Into Our Homestay</h2>
              <p className="section-subtitle">
                Explore our handpicked room highlights, relaxing outdoor spaces, and captivating beachside ambiance.
              </p>

              {/* Gallery Filter */}
              <div className="d-flex justify-content-center gap-2 flex-wrap mb-4">
                <button
                  className={`gallery-filter-btn ${galleryFilter === "all" ? "active" : ""}`}
                  onClick={() => setGalleryFilter("all")}
                >
                  <span>🌟 All Featured Media</span>
                  <span className="filter-count">{displayFeaturedPool.length}</span>
                </button>
                <button
                  className={`gallery-filter-btn ${galleryFilter === "rooms" ? "active" : ""}`}
                  onClick={() => setGalleryFilter("rooms")}
                >
                  <span>🛏️ Rooms & Suites</span>
                  <span className="filter-count">{roomMediaCount}</span>
                </button>
                <button
                  className={`gallery-filter-btn ${galleryFilter === "surroundings" ? "active" : ""}`}
                  onClick={() => setGalleryFilter("surroundings")}
                >
                  <span>🌴 Surroundings & Beach</span>
                  <span className="filter-count">{surroundingsMediaCount}</span>
                </button>
                {videoMediaCount > 0 && (
                  <button
                    className={`gallery-filter-btn ${galleryFilter === "videos" ? "active" : ""}`}
                    onClick={() => setGalleryFilter("videos")}
                  >
                    <span>🎥 Video Walkthroughs</span>
                    <span className="filter-count">{videoMediaCount}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Gallery Grid */}
            {filteredGallery.length === 0 ? (
              <div className="text-center py-5 bg-white rounded-4 shadow-sm border border-light p-4 my-3">
                <div className="fs-1 mb-2">📸</div>
                <h4 className="fw-bold text-dark mb-1">No media in this category yet</h4>
                <p className="text-muted small mb-0">Switch filters above to view other featured photos.</p>
              </div>
            ) : (
              <div className="row g-3">
                {filteredGallery.map((img, i) => (
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={img.id || i}>
                    <div
                      className="gallery-tile"
                      onClick={() => {
                        setLightboxIndex(i);
                        setLightboxOpen(true);
                      }}
                    >
                      {/* Gallery Tile Image/Video */}

                      {img.mediaType === "video" ? (
                        <div className="position-relative w-100 h-100 bg-dark d-flex align-items-center justify-content-center">
                          <video
                            src={img.url}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            muted
                            preload="metadata"
                          />
                          <div
                            className="position-absolute top-50 start-50 translate-middle rounded-circle bg-danger text-white d-flex align-items-center justify-content-center shadow"
                            style={{ width: "44px", height: "44px", fontSize: "1.2rem" }}
                          >
                            ▶
                          </div>
                        </div>
                      ) : (
                        <img
                          src={img.url}
                          alt={img.title || "Ganesh Holiday Homes Photo"}
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            const currentSrc = e.target.src;
                            if (!e.target.dataset.triedFallback && currentSrc) {
                              e.target.dataset.triedFallback = "1";
                              if (currentSrc.includes("firebasestorage.googleapis.com")) {
                                const matches = currentSrc.match(/\/b\/([^/]+)\/o\/([^?]+)/);
                                if (matches && matches[1] && matches[2]) {
                                  const bName = matches[1];
                                  const path = decodeURIComponent(matches[2]);
                                  e.target.src = `https://storage.googleapis.com/${bName}/${path}`;
                                }
                              }
                            }
                          }}
                        />
                      )}

                      {/* Visible Bottom Caption Bar for Photo Title */}
                      <div className="gallery-tile-caption-bar">
                        <p className="gallery-tile-title-text text-truncate">{img.title}</p>
                      </div>

                      {/* Hover Overlay */}
                      <div className="gallery-tile-overlay">
                        <span className="gallery-tile-action-btn">
                          {img.mediaType === "video" ? "▶ Play Video" : "🔍 View Fullscreen"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Video Tour (If available from backend or hotel data) */}
            {(mediaState.videos?.length > 0 || hotel.videoUrl) && (
              <div className="mt-5 text-center">
                <h3 className="h4 fw-bold mb-3 text-dark">Property Video Walkthrough</h3>
                <p className="text-muted mb-4">
                  {hotel.videoText || "Take a relaxed virtual tour of our holiday home and beach surroundings."}
                </p>
                <div className="video-showcase-box mx-auto" style={{ maxWidth: "820px" }}>
                  <div className="ratio ratio-16x9 rounded-4 overflow-hidden shadow-lg bg-black">
                    <video
                      controls
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      poster={hotel.heroImage}
                    >
                      <source
                        src={
                          (mediaState.videos && mediaState.videos.length > 0
                            ? normalizeMediaUrl(mediaState.videos[0])
                            : null) ||
                          hotel.videoUrl
                        }
                        type="video/mp4"
                      />
                      Your browser does not support video playback.
                    </video>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Lightbox Modal */}
        {lightboxOpen && (
          <div className="lightbox-backdrop" onClick={() => setLightboxOpen(false)}>
            <div
              className="lightbox-content-box"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="lightbox-close-btn"
                onClick={() => setLightboxOpen(false)}
                title="Close (Esc)"
              >
                &times;
              </button>

              <button
                className="lightbox-nav-btn lightbox-nav-left"
                onClick={() =>
                  setLightboxIndex((prev) =>
                    prev > 0 ? prev - 1 : filteredGallery.length - 1
                  )
                }
                title="Previous (Left Arrow)"
              >
                <FaChevronLeft />
              </button>

              {filteredGallery[lightboxIndex]?.mediaType === "video" ? (
                <video
                  controls
                  autoPlay
                  src={filteredGallery[lightboxIndex]?.url}
                  className="lightbox-img"
                  style={{ maxHeight: "75vh", maxWidth: "90vw", borderRadius: "12px" }}
                >
                  Your browser does not support video playback.
                </video>
              ) : (
                <img
                  src={filteredGallery[lightboxIndex]?.url}
                  alt={filteredGallery[lightboxIndex]?.title || "Fullscreen Preview"}
                  className="lightbox-img"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    const currentSrc = e.target.src;
                    if (!e.target.dataset.triedFallback && currentSrc) {
                      e.target.dataset.triedFallback = "1";
                      if (currentSrc.includes("firebasestorage.googleapis.com")) {
                        const matches = currentSrc.match(/\/b\/([^/]+)\/o\/([^?]+)/);
                        if (matches && matches[1] && matches[2]) {
                          const bName = matches[1];
                          const path = decodeURIComponent(matches[2]);
                          e.target.src = `https://storage.googleapis.com/${bName}/${path}`;
                        }
                      }
                    }
                  }}
                />
              )}

              <button
                className="lightbox-nav-btn lightbox-nav-right"
                onClick={() =>
                  setLightboxIndex((prev) =>
                    prev < filteredGallery.length - 1 ? prev + 1 : 0
                  )
                }
                title="Next (Right Arrow)"
              >
                <FaChevronRight />
              </button>

              <div className="text-white mt-3 fw-semibold text-center">
                <div
                  className="fs-5 text-capitalize text-white fw-bold mb-1"
                  style={{ letterSpacing: "0.3px" }}
                >
                  {filteredGallery[lightboxIndex]?.title ||
                    `Photo ${lightboxIndex + 1}`}
                </div>
                <div className="text-white-50 small">
                  Photo {lightboxIndex + 1} of {filteredGallery.length}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 8. Local Attractions & Location Guide */}
        <section id="attractions" className="py-5">
          <div className="container">
            <div className="section-header">
              <span className="section-pill-badge">Explore Shrivardhan</span>
              <h2 className="section-main-title">Nearby Tourist Attractions</h2>
              <p className="section-subtitle">
                Discover historical temples, virgin beaches, cliffs, and water sports all within easy reach of our stay.
              </p>
            </div>

            <div className="row g-4">
              {dynamicAttractions.map((item, idx) => (
                <div className="col-12 col-md-6 col-lg-3" key={idx}>
                  <div className="attraction-card">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="attraction-img"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        const currentSrc = e.target.src;
                        if (!e.target.dataset.triedFallback && currentSrc) {
                          e.target.dataset.triedFallback = "1";
                          if (currentSrc.includes("firebasestorage.googleapis.com")) {
                            const matches = currentSrc.match(/\/b\/([^/]+)\/o\/([^?]+)/);
                            if (matches && matches[1] && matches[2]) {
                              const bName = matches[1];
                              const path = decodeURIComponent(matches[2]);
                              e.target.src = `https://storage.googleapis.com/${bName}/${path}`;
                            }
                          }
                        }
                      }}
                    />
                    <div className="attraction-body">
                      <span className="attraction-distance-badge">
                        <FaMapMarkerAlt className="text-danger" /> {item.distance}
                      </span>
                      <h4 className="h6 fw-bold text-dark mb-2">{item.name}</h4>
                      <p className="text-muted small mb-0">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Google Map Location Box */}
            <div
              className="mt-5 p-4 rounded-4 shadow-sm border"
              style={{ background: "rgba(255, 255, 255, 0.9)", backdropFilter: "blur(12px)" }}
            >
              <div className="row align-items-center g-4">
                <div className="col-12 col-lg-5">
                  <h4 className="fw-bold text-dark mb-2">How to Reach Us</h4>
                  <p className="text-muted small mb-3">
                    Located in Narayan Pakhadi, Shrivardhan. Just 3.5 hours from Pune and 4 hours from Mumbai.
                  </p>
                  <ul className="list-unstyled small text-secondary mb-4">
                    <li className="mb-2">📍 <strong>Address:</strong> Narayan Pakhadi Rd, Shrivardhan, Maharashtra 402110</li>
                    <li className="mb-2">🚗 <strong>Road:</strong> Well-connected via Mumbai-Goa Highway (NH 66)</li>
                    <li className="mb-2">🏖️ <strong>Landmark:</strong> 5-min walk to Shrivardhan Beach</li>
                  </ul>
                  <a
                    href="https://maps.google.com/?q=Ganesh+Holiday+Homes+Shrivardhan"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-danger btn-sm rounded-pill fw-semibold px-3"
                  >
                    🗺️ Open in Google Maps
                  </a>
                </div>

                <div className="col-12 col-lg-7">
                  {hotel.mapEmbedUrl && (
                    <div className="ratio ratio-16x9 rounded-3 overflow-hidden shadow-sm">
                      <iframe
                        src={hotel.mapEmbedUrl}
                        title="Ganesh Holiday Homes Location"
                        loading="lazy"
                        style={{ border: 0 }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 9. Verified Reviews & Testimonials */}
        <section id="testimonials" className="py-5">
          <div className="container">
            <div className="section-header">
              <span className="section-pill-badge">Guest Experiences</span>
              <h2 className="section-main-title">Loved by Over 5,000 Guests</h2>
              <p className="section-subtitle">
                Hear what families, couples, and travelers have to say about their memorable stays with us.
              </p>
            </div>

            <div className="row g-4">
              {(hotel.testimonials || [
                {
                  name: "Rohit Patil",
                  text: "Amazing stay! Very homely and comfortable. The homemade fish fry was incredible and the beach is truly just a 5-min walk away.",
                  rating: 5,
                },
                {
                  name: "Sneha Joshi",
                  text: "Loved the proximity to the beach and the warm hospitality. The rooms were spotless, AC worked great, and host was very polite.",
                  rating: 5,
                },
                {
                  name: "Vipul Shirsat",
                  text: "खूप छान आणि स्वच्छ रूम आहेत. फॅमिलीसाठी एकदम सुरक्षित आणि शांत जागा. नक्की भेट द्या!",
                  rating: 5,
                },
              ]).map((t, i) => (
                <div className="col-12 col-md-4" key={i}>
                  <div className="testimonial-card-modern">
                    <div className="testimonial-quote-icon">“</div>
                    <div className="mb-2">
                      {"⭐".repeat(Math.round(t.rating || 5))}
                    </div>
                    <p className="testimonial-text">"{t.text}"</p>
                    <div className="testimonial-author-wrap">
                      <div className="testimonial-avatar">
                        {t.name ? t.name.charAt(0).toUpperCase() : "G"}
                      </div>
                      <div>
                        <div className="testimonial-name">{t.name}</div>
                        <div className="testimonial-badge">✓ Verified Guest</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-4">
              <a
                href={hotel.contact?.googleReview || "https://g.page/r/CYDCxgRHSYI9EBM/review"}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-dark rounded-pill px-4 py-2 fw-semibold"
              >
                ⭐ Read & Leave a Google Review
              </a>
            </div>
          </div>
        </section>

        {/* 10. Frequently Asked Questions */}
        <section id="faq" className="py-5">

          <div className="container" style={{ maxWidth: "850px" }}>
            <div className="section-header">
              <span className="section-pill-badge">Got Questions?</span>
              <h2 className="section-main-title">Frequently Asked Questions</h2>
              <p className="section-subtitle">
                Everything you need to know about booking, meals, timings, and policies.
              </p>
            </div>

            <div className="faq-list">
              {(hotel.faq || [
                {
                  question: "How far is Shrivardhan Beach from the property?",
                  answer:
                    "The beach is just a 5-minute easy walking distance (approx. 450 meters). You don't need to drive or hunt for parking.",
                },
                {
                  question: "Do you provide home-cooked Konkani meals?",
                  answer:
                    "Yes! We provide fresh, homemade Konkani veg and non-veg (Fish & Chicken thali, Solkadhi) meals upon advance notice so everything is cooked fresh.",
                },
                {
                  question: "What are the standard Check-in and Check-out timings?",
                  answer:
                    "Standard check-in is after 12:00 PM and check-out is before 11:00 AM. Early check-in or late check-out is subject to room availability.",
                },
                {
                  question: "Is secure car parking available?",
                  answer:
                    "Yes, we have free private gated car parking inside the property premises for all our staying guests.",
                },
                {
                  question: "How does payment and booking confirmation work?",
                  answer:
                    "You can reserve your room online with a small token advance of ₹1,000 via UPI QR code. The remaining balance can be paid upon check-in.",
                },
              ]).map((faq, i) => (
                <details className="faq-accordion-item" key={i}>
                  <summary className="faq-summary">
                    <span>{faq.question}</span>
                    <span className="text-danger fs-5">+</span>
                  </summary>
                  <p className="faq-answer">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* 11. Multi-Action Floating Contact Widget */}
        <div className="floating-hub-container">
          {contactOpen && (
            <div className="floating-hub-drawer">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold text-dark mb-0">Need Assistance?</h6>
                <button
                  className="btn-close btn-sm"
                  onClick={() => setContactOpen(false)}
                ></button>
              </div>

              <a
                href={`https://wa.me/${cleanPhone}?text=Hi%20Ganesh%20Holiday%20Homes,%20I%20have%20an%20inquiry`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-success w-100 mb-2 d-flex align-items-center justify-content-center gap-2 rounded-pill fw-semibold"
              >
                <FaWhatsapp /> <span>WhatsApp Host</span>
              </a>

              <a
                href={`tel:${cleanPhone}`}
                className="btn btn-outline-dark w-100 d-flex align-items-center justify-content-center gap-2 rounded-pill fw-semibold"
              >
                <FaPhoneAlt /> <span>Call {hotelPhone}</span>
              </a>
            </div>
          )}

          <button
            onClick={() => setContactOpen(!contactOpen)}
            className="floating-hub-trigger"
            aria-label="Contact Host"
            title="Chat or Call Us"
          >
            {contactOpen ? <FaTimes /> : <FaComments />}
          </button>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </HelmetProvider>
  );
}
